import React, { useCallback, useEffect, useState } from "react";
import { Building2, Eye, FileText, BadgeCheck, XCircle } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import Badge from "../common/Badge";
import { formatMoneyDisplay } from "../../utils/money";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import LeadChat from "../common/LeadChat";
import { SkeletonStack } from "../common/Skeleton";
import { sendPushNotification } from "../../utils/sendPushNotification";
import { STRIPE_ENABLED } from "../../featureFlags";
import { buildQuoteResponseRpcArgs } from "../../utils/publicTokenPayload";
import SignaturePad from "./SignaturePad";

// Capture an audit-trail context for quote acceptance.
// Best-effort: never throws, never blocks the accept flow.
//   - typed_name : free-text name the customer typed at acceptance time
//   - ip         : public IP via ipify (CORS-friendly), null on failure
//   - user_agent : navigator.userAgent
async function captureAcceptanceContext(typedName) {
  let ip = null;
  try {
    const r = await fetch("https://api.ipify.org?format=json", {
      cache: "no-store",
    });
    if (r.ok) {
      const j = await r.json();
      ip = j?.ip || null;
    }
  } catch {
    /* offline / blocked / privacy extension — proceed without IP */
  }
  return {
    typedName: (typedName || "").trim(),
    ip,
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent || null : null,
  };
}

function isCashQuoteRow(quote) {
  return (
    quote?.gst_registered === false &&
    /cash payment option selected/i.test(String(quote?.intro_text || ""))
  );
}

export default function CustomerQuoteView({ token, onBackToApp, refreshMainApp, session }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [quoteRow, setQuoteRow] = useState(null);
  const [leadRow, setLeadRow] = useState(null);
  const [businessRow, setBusinessRow] = useState(null);
  const [error, setError] = useState("");
  const [responding, setResponding] = useState(false);

  // Acceptance-confirmation form — required for ALL accepts (logged-in
  // and guest). Captures typed name + agreement checkbox so the audit
  // trail has a positive intent record beyond just "clicked the button".
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [acceptTypedName, setAcceptTypedName] = useState("");
  const [acceptAgreed, setAcceptAgreed] = useState(false);

  // Guest email input — collected in the same accept-confirmation form.
  // (Legacy showGuestForm + guestName state were removed when the form
  // was unified with the typed-name + agreement-checkbox flow.)
  const [guestEmail, setGuestEmail] = useState("");

  // Signature data URL captured via SignaturePad. Required for accept.
  const [signatureDataUrl, setSignatureDataUrl] = useState("");

  const loadQuote = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError("");

      const { data: payload, error: quoteError } = await supabase
        .rpc("rqp_get_public_quote", { p_token: token });

      if (quoteError) throw quoteError;
      const quote = payload?.quote || null;
      if (!quote) {
        setError("Quote not found.");
        setLoading(false);
        return;
      }

      setQuoteRow(quote);

      setLeadRow(payload?.lead || null);
      setBusinessRow(payload?.business || null);

    } catch (err) {
      setError(err.message || "Failed to load quote.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Detect return from Stripe deposit payment (URL has ?quote=TOKEN&payment=success)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      showToast("Deposit paid. Your job is confirmed!", "success");
      const url = new URL(window.location.href);
      url.searchParams.delete("payment");
      window.history.replaceState({}, "", url.toString());
      // Re-fetch so deposit_paid shows immediately without a page reload
      loadQuote();
    }
  }, [showToast, loadQuote]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  async function handleCustomerResponse(status, guestInfo = null) {
    if (!quoteRow) return;
    const cashQuote = isCashQuoteRow(quoteRow);
    const resolvedGuestInfo = guestInfo || (cashQuote
      ? {
          name: leadRow?.customer_name || quoteRow?.customer_name || "Customer",
          email: leadRow?.customer_email || quoteRow?.customer_email || "",
        }
      : null);

    // Acceptance form gating now happens in the UI (showAcceptForm).
    // handleCustomerResponse is only called once the user has typed their
    // name + agreed. We still tolerate a legacy null guestInfo by bailing.
    if (status === "accepted" && !session && !resolvedGuestInfo) {
      setShowAcceptForm(true);
      return;
    }

    try {
      setResponding(true);
      setShowAcceptForm(false);
      const now = new Date().toISOString();

      const updatePayload = {
        customer_status: status,
        customer_responded_at: now,
        status: status === "accepted" ? "accepted" : "declined",
      };

      // Audit trail — only on accept. Best-effort capture: ip/userAgent
      // failures must not block the accept itself.
      if (status === "accepted") {
        const ctx = cashQuote
          ? {
              typedName: resolvedGuestInfo?.name || "",
              ip: null,
              userAgent:
                typeof navigator !== "undefined" ? navigator.userAgent || null : null,
            }
          : await captureAcceptanceContext(
              acceptTypedName || resolvedGuestInfo?.name || ""
            );
        updatePayload.accepted_at = now;
        updatePayload.acceptance_typed_name = ctx.typedName;
        updatePayload.acceptance_ip = ctx.ip;
        updatePayload.acceptance_user_agent = ctx.userAgent;
        updatePayload.acceptance_signature_data_url = cashQuote ? null : signatureDataUrl || null;
      }

      const { data: responsePayload, error: quoteUpdateError } = await supabase
        .rpc(
          "rqp_submit_quote_response",
          buildQuoteResponseRpcArgs({
            token,
            status,
            guestInfo: resolvedGuestInfo,
            acceptanceContext: {
              typedName: updatePayload.acceptance_typed_name,
              ip: updatePayload.acceptance_ip,
              userAgent: updatePayload.acceptance_user_agent,
              signatureDataUrl: updatePayload.acceptance_signature_data_url,
            },
          })
        );

      if (quoteUpdateError) throw quoteUpdateError;

      if (status === "accepted") {
        // Capture sibling quote ids BEFORE we auto-decline them, so we
        // can fire an "auto-declined" email to those losing tradies.
        const siblingQuoteIds = responsePayload?.sibling_quote_ids || [];

        // Email the winning tradie ("accepted") and any losing tradies
        // ("auto-declined") via send-quote-decision-email. Fire-and-forget
        // — email failures must never block the accept flow. Mirrors the
        // exact pattern used in CustomerDashboard.dispatchDecisionEmail and
        // closes the gap where guest accepts via the public-token URL
        // never triggered any tradie email.
        const appUrl = window.location.origin;
        supabase.functions
          .invoke("send-quote-decision-email", {
            body: { quoteId: quoteRow.id, outcome: "accepted", appUrl },
          })
          .catch(() => { /* non-fatal */ });
        siblingQuoteIds.forEach((sid) => {
          supabase.functions
            .invoke("send-quote-decision-email", {
              body: { quoteId: sid, outcome: "auto-declined", appUrl },
            })
            .catch(() => { /* non-fatal */ });
        });

        // Notify the tradie that their quote was accepted. Works for both
        // guest and logged-in customer accepts — the tradie needs to know
        // a job has been won whether or not the customer has an account.
        // Fire-and-forget: push delivery is best-effort and must never block
        // the customer's payment redirect.
        if (responsePayload?.tradie_profile_id) {
          const acceptorName =
            resolvedGuestInfo?.name ||
            session?.user?.email ||
            "A customer";
          sendPushNotification(responsePayload.tradie_profile_id, {
            title: "Quote accepted \uD83C\uDF89",
            body: `${acceptorName} accepted your quote - tap to view`,
            url: "/",
          });
        }

        // Stripe is gated behind STRIPE_ENABLED until the business bank
        // account arrives. While disabled, accepted quotes skip the
        // deposit redirect entirely and fall through to the local
        // accepted state + success toast.
        if (STRIPE_ENABLED) {
          // Redirect to Stripe for deposit payment
          // Return URL preserves the ?quote= token so the view reloads correctly
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set("payment", "success");
          const returnUrl = currentUrl.toString();

          const { data: checkoutData, error: checkoutErr } =
            await supabase.functions.invoke("create-deposit-session", {
              body: {
                quoteId: quoteRow.id,
                businessId: quoteRow.business_id || null,
                customerId: null,
                returnUrl,
              },
            });

          if (!checkoutErr && checkoutData?.url) {
            window.location.href = checkoutData.url;
            return;
          }

          // Fall through to show local state update
        }
      } else {
        // Email the tradie that their quote was declined. Fire-and-forget.
        supabase.functions
          .invoke("send-quote-decision-email", {
            body: {
              quoteId: quoteRow.id,
              outcome: "declined",
              appUrl: window.location.origin,
            },
          })
          .catch(() => { /* non-fatal */ });
      }

      setQuoteRow((prev) =>
        prev
          ? {
              ...prev,
              ...(responsePayload?.quote || {}),
              customer_status: status,
              customer_responded_at: now,
              status: status === "accepted" ? "accepted" : "declined",
            }
          : prev
      );

      await refreshMainApp?.();

      // When STRIPE_ENABLED is false, "accepted" reaches this branch via
      // the happy path (no Stripe redirect was attempted). When it is
      // true, "accepted" only reaches here if the deposit-session call
      // failed — in that case we surface an error so the customer knows
      // payment did NOT go through.
      const acceptedToastMsg = STRIPE_ENABLED
        ? "Quote accepted — but deposit payment could not be started. Please contact the business."
        : "Quote accepted. The business will be in touch shortly.";
      showToast(
        status === "accepted" ? acceptedToastMsg : "Quote declined.",
        status === "accepted"
          ? (STRIPE_ENABLED ? "error" : "success")
          : "info"
      );
    } catch (err) {
      showToast(err.message || "Failed to submit response.", "error");
    } finally {
      setResponding(false);
    }
  }

  if (loading) {
    return (
      <div className="page customer-shell">
        <Card>
          <SkeletonStack lines={5} widths={["55%", "100%", "90%", "85%", "40%"]} />
        </Card>
      </div>
    );
  }

  if (error || !quoteRow) {
    return (
      <div className="page customer-shell">
        <Card>
          <h2>Quote unavailable</h2>
          <p className="muted">{error || "Quote not found."}</p>
          <div className="button-row">
            <Button secondary onClick={onBackToApp}>
              Back to app
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const lineItems = Array.isArray(quoteRow.line_items) ? quoteRow.line_items : [];
  const isCashQuote = isCashQuoteRow(quoteRow);
  const isFinal =
    quoteRow.customer_status === "accepted" ||
    quoteRow.customer_status === "declined";

  return (
    <div className="page customer-shell">
      <div className="two-grid">
        <Card className="hero dark">
          <Badge>Customer quote</Badge>
          <h1>Review your quote</h1>
          <p>
            Please review the details below and let us know if you would like to
            accept or decline this quote.
          </p>

          <div className="button-row">
            <Badge>Status: {quoteRow.customer_status || "pending"}</Badge>
            {quoteRow.viewed_at && (
              <Badge>
                <Eye size={14} /> Viewed
              </Badge>
            )}
          </div>
        </Card>

        <Card>
          <h2>
            <Building2 size={18} /> {businessRow?.name || "Business quote"}
          </h2>

          <p className="muted">
            {leadRow?.trade || "Trade"} - {leadRow?.project_type || "Project"}
            {leadRow?.suburb ? ` ~ ${leadRow.suburb}` : ""}
          </p>

          <div className="estimate-box">
            {quoteRow.gst_registered !== false ? (
              <>
                <div className="muted small">Subtotal</div>
                <div className="estimate-range">
                  {formatMoneyDisplay(quoteRow.subtotal || 0)}
                </div>

                <div className="muted small">GST</div>
                <div className="estimate-range">
                  {formatMoneyDisplay(quoteRow.gst || 0)}
                </div>

                <div className="muted small">Total (inc GST)</div>
                <div className="estimate-range">
                  <strong>{formatMoneyDisplay(quoteRow.total || 0)}</strong>
                </div>
              </>
            ) : (
              <>
                <div className="muted small">Total</div>
                <div className="estimate-range">
                  <strong>{formatMoneyDisplay(quoteRow.total || 0)}</strong>
                </div>
                <p className="muted small" style={{ margin: "8px 0 0" }}>
                  {isCashQuote
                    ? "Cash payment option selected. No GST invoice will be created."
                    : "GST not applicable — supplier not GST registered."}
                </p>
              </>
            )}
          </div>
        </Card>
      </div>

      <div className="two-grid">
        <Card>
          <h3>
            <FileText size={18} /> Quote breakdown
          </h3>

          {lineItems.length > 0 ? (
            <div className="lead-list">
              {lineItems.map((item, index) => (
                <div key={index} className="lead-card">
                  <div className="lead-top">
                    <div className="lead-name">
                      {item.description || "Line item"}
                    </div>
                    <div className="feature-title">
                      {formatMoneyDisplay(item.amount || 0)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">No line items were provided.</p>
          )}

          <div className="estimate-box">
            <div className="muted small">Quote notes / assumptions</div>
            <div className="muted">
              {quoteRow.intro_text || "No additional notes provided."}
            </div>
          </div>
        </Card>

        <Card>
          <h3>
            <BadgeCheck size={18} /> Your decision
          </h3>

          {quoteRow.customer_status === "accepted" && (
            <div className="estimate-box">
              <div className="estimate-range">
                <strong>Accepted</strong>
              </div>
              <p className="muted">Thank you. This quote has been accepted.</p>
              {quoteRow.deposit_paid && (
                <p className="muted">
                  Deposit received:{" "}
                  <strong>{formatMoneyDisplay(quoteRow.deposit_amount || 0)}</strong>
                </p>
              )}
            </div>
          )}

          {quoteRow.customer_status === "declined" && (
            <div className="estimate-box">
              <div className="estimate-range">
                <strong>Declined</strong>
              </div>
              <p className="muted">This quote has been declined.</p>
            </div>
          )}

          {!isFinal && !showAcceptForm && (
            <>
              <p className="muted">
                {isCashQuote
                  ? "Accept this cash quote and the business will be notified."
                  : "If you accept this quote, the business will be notified and the job can proceed."}
              </p>
              <div className="button-row">
                <Button
                  onClick={() =>
                    isCashQuote
                      ? handleCustomerResponse("accepted")
                      : setShowAcceptForm(true)
                  }
                  disabled={responding}
                >
                  <BadgeCheck size={16} /> Accept quote
                </Button>

                <Button
                  secondary
                  onClick={() => handleCustomerResponse("declined")}
                  disabled={responding}
                >
                  <XCircle size={16} /> Decline quote
                </Button>
              </div>
            </>
          )}

          <div className="button-row">
            <Button secondary onClick={onBackToApp}>
              Back to app
            </Button>
          </div>
        </Card>
      </div>

      {showAcceptForm && !isFinal && !isCashQuote && (
        <Card>
          <h3 style={{ margin: "0 0 6px" }}>
            <BadgeCheck size={18} /> Confirm acceptance
          </h3>
          <p className="muted" style={{ margin: "0 0 18px" }}>
            Accepting this quote for{" "}
            <strong>{formatMoneyDisplay(quoteRow?.total)}</strong>
            {quoteRow?.gst_registered !== false ? " (inc GST)" : ""} from{" "}
            <strong>{businessRow?.business_name || businessRow?.name || "the business"}</strong>.
          </p>

          <div className="form-grid" style={{ marginBottom: "12px" }}>
            <div>
              <label>Type your full name *</label>
              <input
                type="text"
                placeholder="Your full name"
                value={acceptTypedName}
                onChange={(e) => setAcceptTypedName(e.target.value)}
              />
            </div>
            {!session && (
              <div>
                <label>Your email *</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                />
              </div>
            )}
          </div>

          <div style={{ marginBottom: "14px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: 600,
                color: "var(--text)",
              }}
            >
              Sign here *
            </label>
            <SignaturePad
              onChange={setSignatureDataUrl}
              height={170}
            />
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              fontSize: "0.9rem",
              lineHeight: 1.5,
              marginBottom: "16px",
              cursor: "pointer",
              color: "var(--text)",
            }}
          >
            <input
              type="checkbox"
              checked={acceptAgreed}
              onChange={(e) => setAcceptAgreed(e.target.checked)}
              style={{ marginTop: "3px", flexShrink: 0 }}
            />
            <span>
              I agree to the quote terms. My acceptance is recorded with
              timestamp, device info, and the signature above.
            </span>
          </label>

          <div className="button-row">
            <Button
              onClick={() => {
                const name = acceptTypedName.trim();
                if (!name) {
                  showToast("Please type your full name.", "error");
                  return;
                }
                if (!signatureDataUrl) {
                  showToast("Please add your signature.", "error");
                  return;
                }
                if (!acceptAgreed) {
                  showToast(
                    "Please tick the agreement to continue.",
                    "error"
                  );
                  return;
                }
                if (!session) {
                  if (!guestEmail.trim() || !guestEmail.includes("@")) {
                    showToast("Please enter a valid email.", "error");
                    return;
                  }
                }
                const guestInfo = !session
                  ? { name, email: guestEmail.trim() }
                  : { name };
                handleCustomerResponse("accepted", guestInfo);
              }}
              disabled={responding}
            >
              <BadgeCheck size={16} /> Confirm acceptance
            </Button>
            <Button
              secondary
              onClick={() => setShowAcceptForm(false)}
              disabled={responding}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <h3 style={{ margin: "0 0 10px", fontSize: "1rem" }}>Messages</h3>
        <LeadChat
          leadId={quoteRow.lead_id}
          senderRole="customer"
          senderName={(leadRow?.customer_name || quoteRow?.customer_name || "Customer").split(" ")[0]}
          businessId={quoteRow.business_id || null}
        />
      </Card>
    </div>
  );
}
